import {useEffect, useState} from "react"
import {createUseStyles} from 'react-jss'
import {useAppDispatch, useAppSelector} from "../redux/hooks"
import {Employee} from "../redux/types"
import FullScreenLoadingIndicator from "../components/FullScreenLoadingIndicator"
import Card from "../components/Card"
import useEmployeeActions from "../hooks/useEmployeeActions"
import {errorHandled} from "../redux/slices/employeeSlice"
import {useTranslation} from "react-i18next"
import useDidUpdateEffect from "../hooks/useDidUpdateEffect"
import ErrorModal from "../components/ErrorModal"
import {useParams} from "react-router-dom";
import InfoCard from "../components/InfoCard"
import {Colors} from "../constants/colors"
import {formatDateString, formatDateTimeString} from "../util/date"
import EditEmployeeModal from "../components/Employee/EditEmployeeModal"
import EmploymentContractCard from "../components/Employee/EmploymentContractCard"
import BackButton from "../components/BackButton"
import EditableTextArea from "../components/EditableTextArea";
import {useEmployeeEditor} from "../components/Employee/useEmployeeEditor";
import EditPayoutLimitationsModal from "../components/Employee/EditPayoutLimitationsModal";

const EmployeeDetails = () => {
  const styles = useStyles()
  const {t} = useTranslation(['employees', 'common']);
  const {employeeId} = useParams()
  const dispatch = useAppDispatch()

  const [employee, setEmployee] = useState<Employee>()
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false)
  const [payoutLimitationsModalVisible, setPayoutLimitationsModalVisible] = useState<boolean>(false)
  const [errorModalVisible, setErrorModalVisible] = useState<boolean>(false)

  const employeeEditor = useEmployeeEditor(employee);

  const {getEmployees, updateEmployee} = useEmployeeActions()
  const loading = useAppSelector(state => state.employee.loading)
  const employees = useAppSelector(state => state.employee.employees)
  const error = useAppSelector(state => state.employee.error)

  useEffect(() => {
    if (employees.length === 0) {
      getEmployees()
    }
  }, [])

  useEffect(() => {
    if (employees.length > 0) {
      var emp = employees.find(x => x.id == employeeId)
      if (emp != null) {
        setEmployee(emp)
      }
    }
  }, [employees, employeeId])

  useDidUpdateEffect(() => {
    if (error) {
      setErrorModalVisible(true)
    }
  }, [error])

  const handleError = () => {
    dispatch(errorHandled())
    setErrorModalVisible(false)
  }

  const handleEdited = () => {
    if (!employee || !employeeEditor.isValid) {
      return
    }
    updateEmployee(employee.id, employee.externalId, employeeEditor.employee)
    setEditModalVisible(false)
    setPayoutLimitationsModalVisible(false)
  }

  const handleInternalCommentEdited = (comment: string) => {
    const result = employeeEditor.handleChange("internalComment", comment.length > 0 ? comment : null)

    if (!employee || !result.isValid) {
      return
    }

    updateEmployee(employee.id, employee.externalId, result.employee)
  }

  const renderEmploymentContracts = () => {
    return employee?.employmentContracts.map((x, index) => {
      return (
        <EmploymentContractCard key={`employment_${index}`} employmentContract={x}/>
      )
    })
  }

  return (
    <>
      <BackButton/>
      <Card className={styles.card}>
        <h2>
          {t('employeeNameTemplate', {ns: 'employees', employee: `${employee?.givenName} ${employee?.familyName}`})}
        </h2>
      </Card>
      <div className={styles.infoCardContainer}>
        <InfoCard title={t('information', {ns: 'common'})} className={styles.infoCard}>
          <div className={styles.row}>
            <div className={styles.label}>{t('id', {ns: 'common'})}</div>
            <div>{employee?.externalId}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('employmentNumber', {ns: 'employees'})}</div>
            <div>{employee?.employmentNumber ?? '-'}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('idNumber', {ns: 'common'})}</div>
            <div>{employee?.identificationNumber}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('name', {ns: 'common'})}</div>
            <div>{employee?.givenName} {employee?.familyName}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('type', {ns: 'common'})}</div>
            <div>{t(`type_${employee?.employeeType}`, {ns: 'employees'})}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('email', {ns: 'common'})}</div>
            <div>{employee?.email ?? '-'} </div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('phoneNumber', {ns: 'common'})}</div>
            <div>{employee?.phoneNumber ?? '-'} </div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('bankAccount', {ns: 'common'})}</div>
            <div>{employee?.bankAccountNumber ?? '-'} </div>
          </div>
        </InfoCard>
        <InfoCard title={t('metadata', {ns: 'employees'})} className={styles.infoCard}>
          <div className={styles.row}>
            <div className={styles.label}>{t('category', {ns: 'common'})}</div>
            <div>{employee?.category ?? '-'}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('department', {ns: 'common'})}</div>
            <div>{employee?.department ?? '-'}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('location', {ns: 'common'})}</div>
            <div>{employee?.location ?? '-'}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('costCenter', {ns: 'common'})}</div>
            <div>{employee?.costCenter ?? '-'}</div>
          </div>
        </InfoCard>
        <InfoCard title={t('taxSettings', {ns: 'employees'})} className={styles.infoCard}>
          <div className={styles.row}>
            <div className={styles.label}>{t('taxTable', {ns: 'employees'})}</div>
            <div>{employee?.taxSettings?.taxTableIdentifier ?? '-'}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('fixTaxPercentage', {ns: 'employees'})}</div>
            <div>{employee?.taxSettings?.fixedTaxRatePercent != null ? `${employee?.taxSettings?.fixedTaxRatePercent?.toString()}%` : '-'}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('payoutTax', {ns: 'employees'})}</div>
            <div>{employee?.taxSettings?.payoutTaxInPercent?.toString()}%</div>
          </div>
        </InfoCard>
        <InfoCard title={t('status', {ns: 'common'})} className={styles.infoCard} enableEdit={true}
                  onEditClick={() => setEditModalVisible(true)}>
          <div className={styles.row}>
            <div className={styles.label}>{t('status', {ns: 'common'})}</div>
            <div>{employee?.status && t(`employeeStatusDescription_${employee?.status}`, {ns: 'employees'})}</div>
          </div>
          {employee?.status == "active" &&
              <div className={styles.row}>
                  <div className={styles.label}>{t('activationDate', {ns: 'employees'})}</div>
                  <div>{formatDateString(employee?.activationDate)}</div>
              </div>
          }
          <div className={styles.row}>
            <div className={styles.label}>{t('paused', {ns: 'employees'})}</div>
            <div>{employee?.paused ? t('yes', {ns: 'common'}) : t('no', {ns: 'common'})}</div>
          </div>
          {employee?.paused &&
              <>
                  <div className={styles.row}>
                      <div className={styles.label}>{t('pauseDate', {ns: 'employees'})}</div>
                      <div>{formatDateTimeString(employee?.pauseDate)}</div>
                  </div>
                  <div className={styles.row}>
                      <div className={styles.label}>{t('pauseReason', {ns: 'employees'})}</div>
                      <div>{employee?.pauseReason}</div>
                  </div>
              </>
          }
          <div className={styles.row}>
            <div className={styles.label}>{t('nonEligible', {ns: 'employees'})}</div>
            <div>{employee?.nonEligible ? t('yes', {ns: 'common'}) : t('no', {ns: 'common'})}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('underForeclosure', {ns: 'employees'})}</div>
            <div>{employee?.underForeclosure ? t('yes', {ns: 'common'}) : t('no', {ns: 'common'})}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('extendTerminationToUpcomingPayday', {ns: 'employees'})}</div>
            <div>{employee?.extendTerminationToUpcomingPayday ? t('yes', {ns: 'common'}) : t('no', {ns: 'common'})}</div>
          </div>
        </InfoCard>
      </div>
      <div className={styles.infoCardContainer}>
        <InfoCard title={t('payoutLimitations', {ns: 'employees'})} className={styles.infoCard} enableEdit={true} onEditClick={() => setPayoutLimitationsModalVisible(true)}>
          <div className={styles.row}>
            <div className={styles.label}>{t('displayPayoutLimitationInfo', {ns: 'employees'})}</div>
            <div>{employee?.displayPayoutLimitationInfo ? t('yes', {ns: 'common'}) : t('no', {ns: 'common'})}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('maxAmountPerPayday', {ns: 'employees'})}</div>
            <div>{employee?.payoutSettings?.maxAmountPerPayday ?? t('noLimitationTemplate', {ns: 'employees', employerValue: employee?.effectivePayoutSettings?.maxAmountPerPayday?.toLocaleString('sv-SE')})}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('maxPercentagePerPayday', {ns: 'employees'})}</div>
            <div>{employee?.payoutSettings?.maxPercentagePerPayday?.toString() ?? t('noLimitationTemplate', {ns: 'employees', employerValue: `${employee?.effectivePayoutSettings?.maxPercentagePerPayday}%`})}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('maxCustomPercentagePerPayday', {ns: 'employees'})}</div>
            <div>{employee?.payoutSettings?.maxCustomPercentagePerPayday?.toString() ?? t('noLimitationTemplate', {ns: 'employees', employerValue: `${employee?.effectivePayoutSettings?.maxCustomPercentagePerPayday}%`})}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>{t('reason', {ns: 'employees'})}</div>
            <div>{employee?.payoutLimitationReason?.toString() ?? t('noReasonSpecified', {ns: 'employees'})}</div>
          </div>
        </InfoCard>
      </div>

      <div className={styles.infoCardContainer}>
        <InfoCard title={t('notes', {ns: 'employees'})} className={styles.infoCard}>
          <div className={styles.row}>
            <EditableTextArea
              value={employee?.internalComment ?? ''}
              onEdited={handleInternalCommentEdited}/>
          </div>
        </InfoCard>
      </div>

      {renderEmploymentContracts()}
      <EditEmployeeModal
        key='editModal'
        editor={employeeEditor}
        visible={editModalVisible}
        onCancelClick={() => {
          employeeEditor.reset();
          setEditModalVisible(false)
        }}
        onSaveClick={handleEdited}
      />
      <EditPayoutLimitationsModal
        key='editPayoutLimitationsModal'
        editor={employeeEditor}
        visible={payoutLimitationsModalVisible}
        onCancelClick={() => {
          employeeEditor.reset();
          setPayoutLimitationsModalVisible(false)
        }}
        onSaveClick={handleEdited}
      />
      <ErrorModal
        visible={errorModalVisible}
        errorText={error?.toString()}
        onCloseClick={handleError}
      />
      <FullScreenLoadingIndicator visible={loading}/>
    </>
  )
}

export default EmployeeDetails

const useStyles = createUseStyles({
  card: {
    marginBottom: 20
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 10,
    marginBottom: 20,
  },
  infoCardContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    columnGap: 20
  },
  infoCard: {
    flex: 1
  },
  row: {
    flex: 1,
    marginBottom: 10,
  },
  label: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 10,
    textTransform: 'uppercase',
    fontSize: 13,
    color: Colors.gray3
  },
  button: {
    alignSelf: 'flex-end'
  }
})
